import { Preferences } from '@capacitor/preferences'
import { v4 as uuid } from 'uuid'

import { sleep } from '@last/core'

import { syncApi } from '@/api/client'
import { rum } from '@/monitoring'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { useDeliveryCompaniesStore } from '@/store/deliveryCompanies'
import { pinia } from '@/store/pinia'
import { usePromotionsStore } from '@/store/promotions'
import { useReservationsStore } from '@/store/reservations'
import { useStatusStore } from '@/store/status'
import { useTabsStore } from '@/store/tabs'
import { useTillStore } from '@/store/till'
import eventList from '@/sync/eventList'
import type { Event, EventDataMap, EventName, RemoteEvent } from '@/types/events'

type SentEvent = { id: number; event: Event }

export class SyncEngine {
  busy = false
  resyncNeeded = false
  outbox: Event[] = []
  pendingRemote: RemoteEvent[] = []
  sentEvents: SentEvent[] = []
  observers: (() => void)[] = []
  currentEventId = 0
  fullResync = false

  async init() {
    const data = await Preferences.get({ key: 'events' })
    if (data.value) {
      this.outbox = JSON.parse(data.value)
    }
    this.kick()
  }

  record<K extends EventName>(eventName: K, data: EventDataMap[K]) {
    const configStore = useConfigStore(pinia)
    const authStore = useAuthStore(pinia)
    const event: Event = {
      uuid: uuid(),
      deviceId: configStore.device.id,
      name: eventName,
      timestamp: Date.now(),
      employeeId: authStore.currentEmployeeId,
      data
    }
    this.play(event, true)
    this.outbox.push(event)
    this.storeOutbox().then(() => this.kick())
  }

  playRemote(event: RemoteEvent) {
    this.pendingRemote.push(event)
    this.kick()
  }

  resync() {
    const auth = useAuthStore(pinia)
    if (!auth.isAuthenticated || !auth.locationId) return

    this.resyncNeeded = true
    this.kick()
  }

  observeEnd(callback: () => void) {
    if (this.busy || this.hasWork()) {
      this.observers.push(callback)
    } else {
      callback()
    }
  }

  isSendingEvents() {
    return this.busy && this.outbox.length > 0
  }

  isInitialized() {
    return this.currentEventId > 0
  }

  hasEvent(event: RemoteEvent) {
    return this.sentEvents.some(e => e.id === event.id)
  }

  private hasWork() {
    return this.outbox.length > 0 || this.resyncNeeded || this.pendingRemote.length > 0
  }

  private async kick() {
    if (this.busy) return
    if (!this.hasWork()) return

    this.busy = true

    do {
      try {
        await this.drainOutbox()

        if (this.resyncNeeded) {
          await this.doResync()
          this.resyncNeeded = false
          continue
        }

        this.processPendingRemote()
      } catch (error: any) {
        useStatusStore(pinia).updateSyncStatus(false)
        if (error.name === 'NetworkError') {
          await sleep(5_000)
        } else {
          rum.addError(error)
          this.fullResync = true
          this.resyncNeeded = true
        }
      }
    } while (this.hasWork())

    useStatusStore(pinia).updateSyncStatus(true)
    this.busy = false
    this.notifyObservers()
  }

  private async drainOutbox() {
    while (this.outbox.length > 0) {
      const event = this.outbox[0]
      const response = await syncApi.playEvent({ playEventRequest: event })
      this.sentEvents.push({ id: response.id, event })
      this.outbox.shift()
      await this.storeOutbox()
    }
  }

  private async doResync() {
    const currentEventId =
      this.currentEventId > 0 && !this.fullResync ? this.currentEventId : undefined
    const data = await syncApi.sync({ currentEventId })

    if ('events' in data) {
      this.pendingRemote.unshift(...data.events)
    } else {
      const tabsStore = useTabsStore(pinia)
      const reservationsStore = useReservationsStore(pinia)
      const tillStore = useTillStore(pinia)
      const promotionsStore = usePromotionsStore(pinia)
      const deliveryCompaniesStore = useDeliveryCompaniesStore(pinia)

      tabsStore.refreshCurrentTabs(data.tabs)
      reservationsStore.refreshReservations(data.reservations)
      tillStore.refreshStartedShifts(data.startedShifts)
      tillStore.refreshStartedShiftsWithCashAmount(data.startedShiftsWithCashAmount)
      tillStore.refreshShiftsEnabled(data.shifts.enabled)
      tillStore.refreshLastDay(data.shifts.lastDay)
      promotionsStore.refreshTabPromotions(data.tabPromotions)
      deliveryCompaniesStore.refreshClosingTimes(data.virtualBrandsClosingTimes)

      const newCurrentEventId = data.currentEventId
      this.currentEventId = newCurrentEventId
      this.replaySentEvents(newCurrentEventId)
    }

    this.fullResync = false
  }

  private replaySentEvents(snapshotEventId: number) {
    this.sentEvents = this.sentEvents.filter(e => e.id > snapshotEventId)
    this.sentEvents.forEach(e => this.play(e.event, true))
  }

  private processPendingRemote() {
    while (this.pendingRemote.length > 0) {
      const event = this.pendingRemote[0]

      if (event.id <= this.currentEventId) {
        this.pendingRemote.shift()
        continue
      }

      if (event.id > this.currentEventId + 1) {
        this.resyncNeeded = true
        return
      }

      this.pendingRemote.shift()
      if (!this.hasEvent(event)) {
        this.play(event, false)
      }
      this.sentEvents = this.sentEvents.filter(e => e.id > event.id)
      this.currentEventId = event.id
    }
  }

  private notifyObservers() {
    const callbacks = this.observers
    this.observers = []
    callbacks.forEach(cb => cb())
  }

  private play(event: Event, local: boolean) {
    const data = JSON.parse(JSON.stringify(event.data))
    if (event.name in eventList) {
      eventList[event.name](data, local)
    }
  }

  private storeOutbox() {
    return Preferences.set({ key: 'events', value: JSON.stringify(this.outbox) })
  }
}
